import { useState, useEffect } from 'react';

/**
 * Custom hook that counts down to the next Friday at 7PM EST
 * @returns {Object} countdown - Object containing days, hours, minutes, seconds
 */
export function useCountdown() {
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Get current date
      const now = new Date();
      
      // Find the next Friday (day 5)
      const nextFriday = new Date();
      // Calculate days until next Friday (0-6)
      let daysUntilFriday = (5 - now.getDay() + 7) % 7;
      // If today is Friday and we've already passed 7PM, we want NEXT Friday
      // We'll handle that time check later, so if today is Friday, get next Friday
      if (daysUntilFriday === 0) daysUntilFriday = 7;
      nextFriday.setDate(now.getDate() + daysUntilFriday);
      
      // Set time to 7PM EST
      // Convert EST to local timezone
      // EST is UTC-5 (or UTC-4 during daylight saving time)
      const isDST = () => {
        // This is a simple way to detect DST in the US
        const jan = new Date(now.getFullYear(), 0, 1).getTimezoneOffset();
        const jul = new Date(now.getFullYear(), 6, 1).getTimezoneOffset();
        return Math.max(jan, jul) !== now.getTimezoneOffset();
      };
      
      const estOffset = isDST() ? -5 : -6; // Hours from UTC
      const localOffset = -(now.getTimezoneOffset() / 60);
      const hourDifference = localOffset - estOffset;
      
      nextFriday.setHours(19 - hourDifference, 0, 0, 0); // 7PM EST converted to local time
      
      // If we've already passed 7PM on Friday, get next Friday
      if (now > nextFriday) {
        nextFriday.setDate(nextFriday.getDate() + 7);
      }
      
      // Calculate the difference
      const difference = nextFriday.getTime() - now.getTime();
      
      // Don't go below zero
      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        };
      }
      
      // Calculate days, hours, minutes, seconds
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    };

    // Initial calculation
    setCountdown(calculateTimeLeft());
    
    // Set up interval to update countdown every second
    const timer = setInterval(() => {
      setCountdown(calculateTimeLeft());
    }, 1000);
    
    // Clean up interval on unmount
    return () => clearInterval(timer);
  }, []);
  
  return countdown;
}